import React, { useMemo } from 'react';
import { Search } from 'lucide-react';

const SearchAutocomplete = ({ 
  query, 
  searchResults = [], 
  onSuggestionSelect,
  maxSuggestions = 5 
}) => {
  const getSuggestionType = (suggestion) => {
    if (suggestion.includes(':')) return 'prefix';
    if (['active', 'maintenance', 'available', 'driving', 'off duty'].includes(suggestion.toLowerCase())) {
      return 'status';
    }
    return 'term';
  };

  const suggestions = useMemo(() => {
    if (!query.trim() || query.length < 2) return [];

    const uniqueSuggestions = new Set();
    const lowerQuery = query.toLowerCase();

    // Generate suggestions from search results
    searchResults.forEach(result => {
      const title = result.title?.toLowerCase();
      const subtitle = result.subtitle?.toLowerCase();
      
      // Add exact matches and partial matches
      if (title) {
        const words = title.split(/\s+/);
        words.forEach(word => {
          if (word.startsWith(lowerQuery) && word !== lowerQuery) {
            uniqueSuggestions.add(word);
          }
        });
      }

      // Add type-based suggestions
      if (result.type) {
        const typePrefix = `${result.type}:`;
        if (typePrefix.toLowerCase().startsWith(lowerQuery)) {
          uniqueSuggestions.add(typePrefix);
        }
      }

      // Add status-based suggestions for vehicles/drivers
      if (result.data) {
        const { status, licenseStatus, hosStatus } = result.data;
        [status, licenseStatus, hosStatus].forEach(statusValue => {
          if (statusValue && statusValue.toLowerCase().includes(lowerQuery)) {
            uniqueSuggestions.add(statusValue);
          }
        });
      }
    });

    // Add common search prefixes
    const commonPrefixes = [
      'driver:', 'vehicle:', 'document:',
      'status:', 'active', 'maintenance', 'available', 'driving'
    ];
    
    commonPrefixes.forEach(prefix => {
      if (prefix.toLowerCase().startsWith(lowerQuery)) {
        uniqueSuggestions.add(prefix);
      }
    });

    return Array.from(uniqueSuggestions)
      .slice(0, maxSuggestions)
      .map(suggestion => ({
        text: suggestion,
        displayText: suggestion,
        type: getSuggestionType(suggestion)
      }));
  }, [query, searchResults, maxSuggestions]);

  const getIconForSuggestion = (type) => {
    switch (type) {
      case 'prefix':
        return <Search className="h-4 w-4 text-blue-500" />;
      case 'status':
        return <div className="w-2 h-2 rounded-full bg-green-500"></div>;
      default:
        return <Search className="h-4 w-4 text-gray-400" />;
    }
  };

  if (suggestions.length === 0) return null;

  return (
    <div className="hidden"> {/* Hidden by default, could be shown on demand */}
      <div className="absolute z-60 w-full mt-12 bg-white rounded-lg shadow-sm border border-gray-100 max-h-32 overflow-y-auto">
        <div className="py-1">
          <div className="px-3 py-1 text-xs text-gray-500 font-medium uppercase tracking-wider">
            Suggestions
          </div>
          {suggestions.map((suggestion, index) => (
            <button
              key={`${suggestion.text}-${index}`}
              onClick={() => onSuggestionSelect(suggestion.text)}
              className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-center space-x-2 text-sm"
            >
              {getIconForSuggestion(suggestion.type)}
              <span className="flex-1">
                <span className="font-medium text-gray-900">
                  {suggestion.displayText}
                </span>
                {suggestion.type === 'prefix' && (
                  <span className="text-gray-500 ml-1">search filter</span>
                )}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchAutocomplete;